import { useCallback, useEffect, useMemo, useState } from 'react';
import { parseIRealIndex, type IRealIndex } from '@/music/ireal/parse';
import { parseTuneIndex } from '@/music/tuneIndex';
import { useRecentsView } from '@/state/historyView';
import { useStore } from '@/state/store';
import { BUNDLED_PLAYLISTS, fetchBundledPlaylist } from './bundledPlaylists';
import { useLibraryTunes } from './libraryTunes';
import { openbookLibrary, type OpenbookLibraryEntry } from './openbookLibrary';
import { usePlaylists } from './playlistAccess';
import { SONG_PRESETS } from './presets';
import { realbookLibrary, type RealbookLibraryEntry } from './realbookLibrary';
import { collectTuneSearchSources } from './tuneSearch';
import { recentTuneRows, type TuneSourceEntry } from './tuneSources';

let bundledIndex: IRealIndex | null = null;
let bundledPending: Promise<IRealIndex> | null = null;

/** A lightweight, session-cached index; individual tunes parse only on load. */
export function loadBundledTuneSearchIndex(): Promise<IRealIndex> {
  if (bundledIndex) return Promise.resolve(bundledIndex);
  if (bundledPending) return bundledPending;
  bundledPending = fetchBundledPlaylist(BUNDLED_PLAYLISTS[0])
    .then((raw) => {
      const index = parseIRealIndex(raw);
      if (!index.songs.length) throw new Error('No chord charts found.');
      bundledIndex = { ...index, name: BUNDLED_PLAYLISTS[0].name };
      return bundledIndex;
    })
    .finally(() => {
      // A failed request is retryable, and concurrent consumers share it.
      bundledPending = null;
    });
  return bundledPending;
}

/** The existing importer persists its raw input outside Zustand. Read it
 * without rehydrating or replacing the active imported playlist merely
 * because somebody opened search. A later live import takes precedence.
 * The import may be an iReal Pro link or an ABC tunebook. */
export function persistedImport(): IRealIndex | null {
  try {
    const raw = localStorage.getItem('vlc:playlist');
    return raw ? parseTuneIndex(raw) : null;
  } catch {
    return null;
  }
}

export interface TuneSearchSources {
  rows: TuneSourceEntry[];
  recentRows: TuneSourceEntry[];
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/** Fetch catalogs once the home page's search is first used. The user's own scores,
 * permitted playlists and recents stay live while those requests finish. */
export function useTuneSearchSources(enabled = true): TuneSearchSources {
  const savedCharts = useStore((state) => state.savedCharts);
  const livePlaylist = useStore((state) => state.playlist);
  const recentTunes = useRecentsView();
  const userPlaylists = usePlaylists();
  const library = useLibraryTunes(enabled);
  const playlist = useMemo(() => livePlaylist ?? persistedImport(), [livePlaylist]);
  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  const [bundled, setBundled] = useState(bundledIndex);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.allSettled([
      openbookLibrary().then((entries) => {
        if (!cancelled) setOpenbook(entries);
      }),
      realbookLibrary().then((entries) => {
        if (!cancelled) setRealbook(entries);
      }),
      loadBundledTuneSearchIndex().then((index) => {
        if (!cancelled) setBundled(index);
      }),
    ]).then((results) => {
      if (cancelled) return;
      setLoading(false);
      if (results.some((result) => result.status === 'rejected')) {
        setError('Some tune collections could not be loaded. You can still search the others.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [attempt, enabled]);

  const inputs = useMemo(
    () => ({
      presets: SONG_PRESETS,
      savedCharts,
      playlist,
      userPlaylists,
      openbook,
      realbook,
      library,
    }),
    [savedCharts, playlist, userPlaylists, openbook, realbook, library],
  );
  const rows = useMemo(() => collectTuneSearchSources(inputs, bundled), [inputs, bundled]);
  const recentRows = useMemo(
    () => recentTuneRows(inputs),
    // recentTuneRows deliberately reads the latest refs at the shared load seam.
    [inputs, recentTunes], // eslint-disable-line react-hooks/exhaustive-deps
  );
  return { rows, recentRows, loading, error, retry };
}

/** Test seam: forget only the search catalog cache, never a personal import. */
export function __resetTuneSearchSourcesForTests(): void {
  bundledIndex = null;
  bundledPending = null;
}
